import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { prs } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

function fmtAmt(n) {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(n || 0));
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Read-only PR detail drawer for the Requestor Review page.
 * Shows header + line items so the requestor can spot bogus content,
 * with inline Approve / Reject actions for pending PRs.
 */
export default function PRReviewDetailDrawer({
  vocNo,
  open,
  onClose,
  reviewState,           // "Pending" | "Approved" | "Rejected"
  requesterRemark,       // current Requester_Remark value (if any)
  prRemark,              // original PR header remark typed at creation time (Reqester_Remark)
  acting,
  onApprove,
  onReject,
}) {
  const [pr, setPR] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !vocNo) return;
    setPR(null);
    setLoading(true);
    prs.get(vocNo)
      .then((r) => setPR(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load PR"))
      .finally(() => setLoading(false));
  }, [vocNo, open]);

  const isPending = reviewState === "Pending";
  const isRejected = reviewState === "Rejected";

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[640px] lg:max-w-[760px] overflow-y-auto p-0"
        data-testid="review-drawer"
      >
        <SheetHeader className="bg-cyan-700 text-white px-5 sm:px-6 py-4">
          <SheetTitle className="text-white text-lg sm:text-xl flex items-center gap-2">
            {loading ? "Loading PR…" : pr ? (
              <>
                PR #{pr.voc_no}
                {pr.currency && pr.currency !== "INR" && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold bg-amber-300 text-amber-900 border border-amber-400 leading-none"
                    title={`Entered in ${pr.currency} · Exch rate ${fmtAmt(pr.exch_rate)}`}
                  >
                    {pr.currency} @ {fmtAmt(pr.exch_rate)}
                  </span>
                )}
              </>
            ) : "PR detail"}
          </SheetTitle>
          {pr && (
            <SheetDescription className="text-sm text-cyan-50 mt-1">
              {pr.plant ? `${pr.plant} · ` : ""}{pr.purc_type} · {fmtDate(pr.vdate)} · {pr.supplier}
            </SheetDescription>
          )}
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-10 px-6">
            <Loader2 className="animate-spin" size={16} /> Loading PR details…
          </div>
        )}

        {pr && !loading && (
          <div className="px-5 sm:px-6 py-5 space-y-5">
            {/* Current review state pill */}
            {!isPending && (
              <div className={`flex items-start gap-2 px-3 py-2 rounded-sm border text-sm ${
                isRejected
                  ? "bg-rose-50 border-rose-200 text-rose-900"
                  : "bg-emerald-50 border-emerald-200 text-emerald-900"
              }`} data-testid="review-state-banner">
                {isRejected ? <XCircle size={16} className="flex-shrink-0 mt-0.5" /> : <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />}
                <div>
                  <div className="font-semibold uppercase tracking-wider text-[11px]">You marked this as {reviewState}</div>
                  {requesterRemark && <div className="text-xs mt-0.5">{requesterRemark}</div>}
                </div>
              </div>
            )}

            {/* Original PR remark — what the requestor typed when creating the PR */}
            {prRemark && (
              <div
                className="flex items-start gap-2 px-3 py-2 rounded-sm border border-amber-200 bg-amber-50 text-amber-900"
                data-testid="pr-remark-banner"
              >
                <span className="text-[10px] font-bold uppercase tracking-wider flex-shrink-0 mt-0.5">PR Remark</span>
                <span className="text-sm">{prRemark}</span>
              </div>
            )}

            {/* Created-by banner (helps spot bogus PRs raised in your name by someone else) */}
            {pr.creator && pr.creator.full_name && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-sm border border-cyan-200 bg-cyan-50 text-cyan-900 text-sm" data-testid="created-by-banner">
                <span className="font-semibold uppercase tracking-wider text-[10px]">Created by</span>
                <span className="font-semibold">{pr.creator.full_name}</span>
                <span className="text-cyan-700 mono text-[11px]">#{pr.creator.emp_no}</span>
                {pr.creator.created_at && (
                  <span className="text-cyan-700 text-[11px] ml-auto">
                    {new Date(pr.creator.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
            )}
            {pr.creator === null && (
              <div className="px-3 py-1.5 rounded-sm border border-slate-200 bg-slate-50 text-slate-500 text-[11px] italic" data-testid="created-by-legacy">
                Created via legacy ERP — creator not recorded.
              </div>
            )}

            {/* Summary tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">PR Amount</div>
                <div className="text-base font-bold text-slate-900 mt-0.5">
                  ₹{fmtAmt(pr.amount)}
                </div>
                {pr.currency !== "INR" && (
                  <div className="text-[11px] text-slate-500">
                    ({pr.currency} {fmtAmt(pr.amount / (pr.exch_rate || 1))} × {fmtAmt(pr.exch_rate)})
                  </div>
                )}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Supplier</div>
                <div className="text-sm font-semibold text-slate-900 mt-0.5 truncate" title={pr.supplier}>{pr.supplier}</div>
                <div className="text-[11px] text-slate-500 mono">{pr.pcode}</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Status</div>
                <div className="text-sm font-semibold text-slate-900 mt-0.5">{pr.req_status || "Open"}</div>
                <div className="text-[11px] text-slate-500">{pr.purc_type}</div>
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Line Items</h3>
                <span className="text-xs text-slate-500">{(pr.items || []).length} item{(pr.items || []).length === 1 ? "" : "s"}</span>
              </div>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-slate-600 text-[11px] uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Item</th>
                      <th className="px-3 py-2 text-right font-semibold">Qty</th>
                      <th className="px-3 py-2 text-right font-semibold">Rate</th>
                      <th className="px-3 py-2 text-right font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pr.items || []).map((it, idx) => (
                      <tr key={`${it.icode}-${idx}`} className="border-t border-slate-200" data-testid={`drawer-item-${idx}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900 leading-tight">{it.iname}</div>
                          <div className="text-[11px] text-slate-500 mono">{it.icode}</div>
                          {/* Creator's per-line-item remark (PoRequesti.Remark) */}
                          {it.remark && (
                            <div
                              className="mt-1 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 italic"
                              data-testid={`drawer-item-remark-${idx}`}
                              title={it.remark}
                            >
                              <span className="not-italic font-bold mr-1">Note:</span>{it.remark}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-700">
                          {fmtAmt(it.qty)} <span className="text-[11px] text-slate-500">{it.unit}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {pr.currency !== "INR" ? (
                            <>
                              <div>{pr.currency} {fmtAmt(it.rate)}</div>
                              <div className="text-[10px] text-slate-500">≈ ₹{fmtAmt(it.rate_inr)}</div>
                            </>
                          ) : (
                            <div>₹{fmtAmt(it.rate)}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900">
                          ₹{fmtAmt(it.amt)}
                        </td>
                      </tr>
                    ))}
                    {(!pr.items || pr.items.length === 0) && (
                      <tr><td colSpan={4} className="px-3 py-4 text-center text-sm text-slate-500">No line items found on this PR.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Approver trail */}
            <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
              {pr.auth_by && <span>Authoriser (L1): <span className="font-semibold text-slate-700 mono">{pr.auth_by}</span>{pr.auth_date ? ` · ${fmtDate(pr.auth_date)}` : " · pending"}</span>}
              {pr.app_by && <span>Approver (L2): <span className="font-semibold text-slate-700 mono">{pr.app_by}</span>{pr.app_date ? ` · ${fmtDate(pr.app_date)}` : " · pending"}</span>}
            </div>

            {/* Action bar */}
            {isPending && (
              <div className="sticky bottom-0 -mx-5 sm:-mx-6 mt-2 px-5 sm:px-6 py-3 bg-white border-t border-slate-200 flex flex-col sm:flex-row gap-2 justify-end" data-testid="drawer-actions">
                <div className="flex items-center text-[12px] text-amber-700 gap-1 mr-auto">
                  <AlertTriangle size={14} /> Confirm only if you genuinely raised this PR.
                </div>
                <Button
                  variant="outline"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={onReject}
                  disabled={acting}
                  data-testid="drawer-reject"
                >
                  <XCircle size={14} className="mr-1" /> Reject
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={onApprove}
                  disabled={acting}
                  data-testid="drawer-approve"
                >
                  {acting ? <Loader2 className="animate-spin" size={14} /> : <><CheckCircle2 size={14} className="mr-1" /> Approve</>}
                </Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
